import { CommissionRule, FareDetails, FormValues, passenger } from "./types";
import * as XLSX from "xlsx";
import dayjs, { Dayjs } from "dayjs";
import { COUNTRY_CODES } from "./constant";
type CreateSSRDetailsInput = {
  selectedMeal: any[];
  selectedBaggage: any[];
  selectedSeats: any[];
  flightSegments: any[];
};

type SSRDetail = {
  FlightNo: string;
  AirlineCode: string;
  Origin: string;
  Destination: string;
  PaxType: number;
  PaxId: number;
  BoundType: string;

  MCode: string;
  MDetail: string;
  MChargeCode: string;
  MCharge: number;
  MStatus: boolean;

  BWeight: string;
  BUnit: string;
  BCode: string;
  BChargeCode: string;
  BCharge: number;
  BStatus: boolean;

  SCode: string;
  SChargeCode: string;
  SCharge: number;
  SStatus: boolean;
};

type CreateSSRDetailsOutput = {
  SSRDetails: SSRDetail[];
};
export function injectSSRDetailsIntoSegments(
  segments: any[],
  selectedSSR: any[]
) {
  if (!Array.isArray(segments)) return [];

  return segments.map((segment: any) => {
    const matchingSSRs = selectedSSR?.filter((ssr: any) =>
      segment?.Bonds?.some((bond: any) =>
        bond?.Legs?.some((leg: any) => leg?.FlightNumber === ssr?.FlightNo)
      )
    );

    return {
      ...segment,
      SSRDetails: matchingSSRs || [],
    };
  });
}
export function injectSSRDetails(bond: any, selectedSSR: any[]) {
  if (!bond || typeof bond !== "object") return;

  const matchingSSRs = selectedSSR?.filter((ssr: any) =>
    bond?.Legs?.some((leg: any) => leg?.FlightNumber === ssr?.FlightNo)
  );

  return matchingSSRs?.[0] || [];
}

export function createSSRDetails({
  selectedMeal = [],
  selectedBaggage = [],
  selectedSeats = [],
  flightSegments = [],
}: CreateSSRDetailsInput) {
  const ssrDetails: any[] = [];
  const ssrMap = new Map<string, any>();
  flightSegments?.forEach((segment) => {
    const bonds = segment?.Bonds || [];
    const boundType = segment?.BoundType || "OutBound";

    bonds.forEach((bond: any) => {
      const legs = bond?.Legs || [];

      legs.forEach((leg: any) => {
        const flightNo = leg?.FlightNumber || segment?.FlightNumber || "";

        // Get relevant items for this leg
        const relevantSeats = selectedSeats.filter(
          (seat) => seat.flightNumber === flightNo
        );
        const relevantMeals = selectedMeal.filter(
          (meal) =>
            meal.flightNumber === flightNo || meal.BoundType === boundType
        );
        const relevantBaggage = selectedBaggage.filter(
          (baggage) =>
            baggage.flightNumber === flightNo || baggage.BoundType === boundType
        );

        const maxPax = Math.max(
          relevantSeats.length,
          relevantMeals.reduce((sum, m) => sum + (m.count || 0), 0),
          relevantBaggage.reduce((sum, b) => sum + (b.count || 0), 0),
          1
        );

        for (let paxIndex = 0; paxIndex < maxPax; paxIndex++) {
          const uniqueKey = `${flightNo}-${paxIndex}`;

          const seat = relevantSeats[paxIndex];

          // Meal distribution
          let meal = null;
          let mealDistributed = 0;
          for (const mealItem of relevantMeals) {
            if (mealDistributed + (mealItem.count || 0) > paxIndex) {
              meal = mealItem;
              break;
            }
            mealDistributed += mealItem.count || 0;
          }

          // Baggage distribution
          let baggage = null;
          let baggageDistributed = 0;
          for (const baggageItem of relevantBaggage) {
            if (baggageDistributed + (baggageItem.count || 0) > paxIndex) {
              baggage = baggageItem;
              break;
            }
            baggageDistributed += baggageItem.count || 0;
          }

          const ssrDetail = {
            FlightNo: flightNo,
            AirlineCode: leg?.AircraftCode || "",
            Origin: leg?.Origin || "",
            Destination: leg?.Destination || "",
            PaxType: 0,
            PaxId: paxIndex + 1,
            BoundType: boundType,

            MCode: meal?.SSRCode || "",
            MDetail: meal?.Detail || "",
            MChargeCode: meal?.PaxDetail?.lstFareDetail?.[0]?.ChargeCode || "",
            MCharge: meal?.Amount || 0,
            MStatus: !!meal,

            BWeight: baggage ? "0" : "",
            BUnit: baggage ? "Kg" : "",
            BCode: baggage?.SSRCode || "",
            BChargeCode:
              baggage?.PaxDetail?.lstFareDetail?.[0]?.ChargeCode || "",
            BCharge: baggage?.Amount || 0,
            BStatus: !!baggage,

            SCode: seat?.SeatNumber || "",
            SChargeCode: seat?.SeatNumber || "",
            SCharge: seat?.SeatFare?.TotalFare || 0,
            SStatus: !!seat,
          };

          if (!ssrMap.has(uniqueKey) || seat || meal || baggage) {
            ssrMap.set(uniqueKey, ssrDetail);
          }
        }
      });
    });
  });

  ssrDetails.push(...Array.from(ssrMap.values()));
  return { SSRDetails: ssrDetails };
}
export function getFlightNumber(
  journeys?: any[],
  journeyIndex?: number,
  legIndex?: number
): string | undefined {
  const segments = journeys?.[journeyIndex ?? 0]?.Segments?.[0] || [];
  for (const bond of segments.Bonds || []) {
    const leg = bond.Legs?.[legIndex ?? 0];

    if (leg) return leg.FlightNumber;
  }
  return undefined;
}

export const extractLegsFromJourney = (journey: any) => {
  // Handle different possible data structures
  if (journey?.Segments) {
    return journey.Segments.flatMap(
      (segment: any) =>
        segment?.Bonds?.flatMap((bond: any) => bond?.Legs || []) || []
    );
  }
  if (journey?.Bonds) {
    return journey.Bonds.flatMap((bond: any) => bond?.Legs || []);
  }
  if (journey?.Legs) {
    return journey.Legs;
  }
  return [];
};

// Helper function to format leg display text
export const formatLegDisplay = (leg: any) => {
  const origin = leg?.Origin || leg?.DepartureAirportCode || "N/A";
  const destination = leg?.Destination || leg?.ArrivalAirportCode || "N/A";
  const flightNum = leg?.FlightNumber || leg?.FlightNo;

  return {
    display: `${origin} – ${destination}`,
    subtitle: flightNum ? `Flight ${flightNum}` : null,
  };
};

// Helper function to calculate total fare
export const calculateTotalFare = (seats: any[]) => {
  return seats.reduce((sum, seat) => {
    const fare =
      seat?.SeatFare?.TotalFare ||
      seat?.TotalFare ||
      seat?.Fare ||
      seat?.Price ||
      0;
    return sum + fare;
  }, 0);
};

// Helper function to get seat display info
export const getSeatDisplayInfo = (seat: any) => {
  const seatNumber = seat?.SeatNumber || seat?.SeatNo || seat?.Number || "N/A";
  const fare =
    seat?.SeatFare?.TotalFare ||
    seat?.TotalFare ||
    seat?.Fare ||
    seat?.Price ||
    0;

  return { seatNumber, fare };
};
export function formatDate(date: string | Date) {
  var d = new Date(date),
    month = "" + (d.getMonth() + 1),
    day = "" + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;
  return [year, month, day].join("-");
}

export function parseDynamicFareRules(fareRule?: string) {
  const rules = fareRule?.split("|") || [];
  const fareDetails: {
    start: number;
    end: number;
    cancelFee?: number;
    changeFee?: number;
  }[] = [];
  let emtFee = 0;

  rules.forEach((rule) => {
    if (rule.startsWith("EMTFee")) {
      emtFee = parseInt(rule.split("-")[1] ?? "0", 10);
      return;
    }

    const [key, value] = rule.split(":");
    const amount = parseInt(value, 10) || 0;

    // Normalize the key to detect CAN or CHG types
    const isCancel = /CAN(CEL)?-BEF/i.test(key);
    const isChange = /CHG(ANGE)?-BEF/i.test(key);

    // Extract time range
    const timeMatch = key.match(/BEF\s*(\d+)_(\d+)/);
    if (!timeMatch) return;

    const [, startStr, endStr] = timeMatch;
    const start = parseInt(startStr, 10);
    const end = parseInt(endStr, 10);

    let entry = fareDetails.find(
      (item) => item.start === start && item.end === end
    );
    if (!entry) {
      entry = { start, end };
      fareDetails.push(entry);
    }

    if (isCancel) entry.cancelFee = amount;
    if (isChange) entry.changeFee = amount;
  });

  return { fareDetails, emtFee };
}

export function parseCustomDateString(input: string) {
  if (!input || typeof input !== "string") return null;

  // Example input: Sat-28Jun2025
  const match = input.match(/^([A-Za-z]{3})-(\d{2})([A-Za-z]{3})(\d{4})$/);

  if (!match) return null;

  const [, day, date, month, year] = match;

  return {
    day, // "Sat"
    date, // "28"
    month, // "Jun"
    year, // "2025"
  };
}

export function calculateFare(paxFares: any[]) {
  let totalBaseFare = 0;
  let totalTax = 0;
  let totalFare = 0;

  paxFares.forEach((pax) => {
    totalBaseFare += pax.BasicFare || 0;
    totalTax += pax.TotalTax || 0;
    totalFare += pax.TotalFare || 0;
  });

  return {
    baseFare: totalBaseFare,
    totalTax: totalTax,
    totalFeeAndSurcharge: totalFare,
  };
}

export const getInitialTravellerValues = (
  adultCount: number,
  childCount: number,
  infantCount: number
) => {
  const getAdult = () => ({
    Title: "",
    FirstName: "",
    LastName: "",
    EmailAddress: "",
    MobileNumber: "",
    countryName: "",
    nationality: "",
    isInternational: false,
    DOB: "",
    PassportNo: "",
    PassportExpiryDate: "",
  });

  const getChild = () => ({
    Title: "",
    FirstName: "",
    LastName: "",
    isInternational: false,
    DOB: "",
    PassportNo: "",
    PassportExpiryDate: "",
  });

  const getInfant = () => ({
    Title: "",
    FirstName: "",
    LastName: "",
    isInternational: false,
    DOB: "",
    PassportNo: "",
    PassportExpiryDate: "",
  });

  return {
    adultTravellers: Array(adultCount).fill(getAdult()),
    ChildTraveller: childCount > 0 ? Array(childCount).fill(getChild()) : [],
    InfantTraveller:
      infantCount > 0 ? Array(infantCount).fill(getInfant()) : [],
  };
};

/**
 * Checks if the user has enough balance to make a payment
 * while keeping at least 20% of the original balance untouched.
 *
 * @param walletBalance - Current wallet balance (number)
 * @param chargeAmount - Amount to charge (number)
 * @returns boolean - true if allowed, false otherwise
 */
export const hasSufficientBalance = (
  walletBalance: number,
  chargeAmount: number
): boolean => {
  if (walletBalance <= 0 || chargeAmount < 0) return false;

  const remainingBalance = walletBalance - chargeAmount;
  const minimumRequired = walletBalance * 0.2;

  return remainingBalance >= minimumRequired;
};

export function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const generateRandomInteger = () => {
  const min = 1000000000; // Minimum 10-digit number
  const max = 9999999999; // Maximum 10-digit number
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export function hasAnyValue(list: any) {
  return list.some((obj: any) =>
    Object.values(obj).some((value) =>
      typeof value === "string" ? value.trim() !== "" : value !== 0
    )
  );
}

interface Feature {
  id: number;
  name: string;
  code: string;
}

interface ModulePermission {
  code: string;
  features: Feature[];
}
type ConditionalCode = {
  code: string;
  parent: string;
};

export function replaceEMTwithEWTS(text: string) {
  // Use global & case-sensitive replace
  return text.replace(/EMT/g, "EWTS");
}

export const extractAllowedCodes = (
  menuPermissions: any[],
  impliedPermissions: Record<string, string[]>,
  conditionalCodes: ConditionalCode[]
): string[] => {
  const directCodes =
    menuPermissions?.flatMap((module) =>
      module.features?.map((feature: any) => feature.code)
    ) || [];

  const impliedCodes = Object.values(impliedPermissions).flat();
  const conditionalIncludedCodes =
    conditionalCodes
      ?.filter((item) => directCodes.includes(item.parent))
      .map((item) => item.code) || [];
  const combined = new Set<string>([
    ...directCodes,
    ...impliedCodes,
    ...conditionalIncludedCodes,
  ]);

  return Array.from(combined);
};

export const calculateCommission = (
  totalFare: number,
  passengers: {
    adultCount: number;
    childrenCount: number;
    infantCount: number;
  },
  commission: {
    type: "percentage" | "flat";
    percentage?: number;
    adultValue: number;
    infantValue: number;
  }
): number => {
  let totalCommission = 0;

  if (commission?.type === "percentage") {
    totalCommission = (totalFare * (commission.percentage || 0)) / 100;
  } else {
    const adultCommission = commission?.adultValue;
    const childCommission = commission?.adultValue;
    const infantCommission = commission?.infantValue;

    totalCommission += passengers?.adultCount * adultCommission + totalFare;
    if (passengers.childrenCount > 0) {
      totalCommission += passengers?.childrenCount * childCommission;
    }
    if (passengers.infantCount > 0) {
      totalCommission += passengers?.infantCount * infantCommission;
    }
  }

  return Math.round(totalCommission);
};

export const calculateFareWithCommission = ({
  baseFare,
  count,
  commissionType,
  commissionPercentage,
  commissionValue,
}: {
  baseFare: number;
  count: number;
  commissionType?: string;
  commissionPercentage?: number;
  commissionValue?: number;
}): number => {
  if (!baseFare || !count) return 0;

  if (commissionType === "percentage") {
    const commission = (baseFare * (commissionPercentage ?? 0)) / 100;
    return Math.round(baseFare + commission);
  } else {
    const total = baseFare * count + (commissionValue ?? 0) * count;
    return Math.round(total);
  }
};

export const exportCommissionToExcel = (commissionData: any[]) => {
  if (!commissionData || commissionData.length === 0) {
    console.warn("No data to export");
    return;
  }

  const rows = commissionData.map((item) => {
    const include =
      item.conditions?.find((c: any) => c.conditionType === "include") || {};
    const exclude =
      item.conditions?.find((c: any) => c.conditionType === "exclude") || {};

    return {
      Type: item.commissionType,
      CRS: item.crsName,
      Airline: item.airlineOfficeId,
      PCC: "", // You can replace this with actual data if available
      Airport: item.airport,
      Category: item.category,
      Cabin: item.cabin,
      "Flat Type": item.flatType,
      "Fare Type": item.fareType,
      "Percentage (%)": item.adultChildValue,
      "Valid For": item.validFor,
      "Com Apply": "", // Placeholder
      Flat: item.per,
      "Travel Valid To": item.validUpto,
      "Include Origin": include.originCodes?.join(", ") || "",
      "Include Destination": include.destinationCodes?.join(", ") || "",
      "Include Sector": include.sector || "",
      "Include RBD": include.rbd || "",
      "Include Flights": include.flights || "",
      "Exclude Origin": exclude.originCodes?.join(", ") || "",
      "Exclude Destination": exclude.destinationCodes?.join(", ") || "",
      "Exclude Flights": exclude.flights || "",
      "Connecting Flight": item.otherOptions?.connectingFlight ? "Yes" : "No",
      Via: item.otherOptions?.via ? "Yes" : "No",
      Trip: item.otherOptions?.tripType || "",
      "Include Fare Description": include.fareDescription || "",
      "Exclude Fare Description": exclude.fareDescription || "",
      "Operating Carrier": include.operatingCarriers || "",
      "Exclude Operating Carrier": exclude.operatingCarriers || "",
      "Include Code": include.code || "",
      "Exclude Code": exclude.code || "",
      "Created Date": item.createdAt?.slice(0, 10),
      "Updated Date": item.updatedAt?.slice(0, 10),
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Commissions");
  XLSX.writeFile(workbook, "Flight_Commissions.xlsx");
};

export function deepOmit<T>(obj: T, keysToOmit: string[]): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => deepOmit(item, keysToOmit)) as unknown as T;
  } else if (typeof obj === "object" && obj !== null) {
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!keysToOmit.includes(key)) {
        result[key] = deepOmit(value, keysToOmit);
      }
    }
    return result as T;
  }
  return obj;
}

function checkDateValidation(
  rule: CommissionRule,
  travelFrom?: string,
  travelTo?: string
): boolean {
  const today = new Date();
  const dv = rule.dateValidation;

  if (!dv) return true;

  if (dv.salesValidFrom && today < new Date(dv.salesValidFrom)) return false;
  if (dv.salesValidTo && today > new Date(dv.salesValidTo)) return false;

  if (
    dv.travelValidFrom &&
    travelFrom &&
    new Date(travelFrom) < new Date(dv.travelValidFrom)
  ) {
    return false;
  }
  if (
    dv.travelValidTo &&
    travelTo &&
    new Date(travelTo) > new Date(dv.travelValidTo)
  ) {
    return false;
  }

  return true;
}

function checkOtherOptions(rule: CommissionRule, tripType: string): boolean {
  const opt = rule.otherOptions;
  if (!opt) return true;

  if (opt.tripType && opt.tripType !== "ALL" && opt.tripType !== tripType) {
    return false;
  }

  return true;
}
// function checkConditions(
//   rule: CommissionRule,
//   origin: string,
//   destination: string
// ): boolean {
//   const conditions = rule.conditions || [];

//   for (const cond of conditions) {
//     const originMatch = cond.originCodes?.includes(origin) ?? false;
//     const destMatch = cond.destinationCodes?.includes(destination) ?? false;

//     if (cond.conditionType === "include") {
//       if (!originMatch || !destMatch) return false;
//     } else if (cond.conditionType === "exclude") {
//       if (originMatch || destMatch) return false;
//     }

//     if (cond.sector) {
//       const sectorMatch = `${origin}-${destination}` === cond.sector;
//       if (cond.conditionType === "include" && !sectorMatch) return false;
//       if (cond.conditionType === "exclude" && sectorMatch) return false;
//     }
//   }

//   return true;
// }

export function extractBookingDataFromFlight(flight: any) {
  const segment = flight?.Bonds?.[0]?.Legs?.[0]; // First leg (important)
  const isRoundTrip =
    flight.BondType === "InBound" || flight.BondType === "Round";

  return {
    origin: segment?.Origin,
    destination: segment?.Destination,
    tripType: isRoundTrip ? "Round Trip" : "One way",
    cabin: segment?.Cabin,
    airline: segment?.CarrierCode,
    fareType: flight.Fare?.fareType ?? undefined,
    isInternational: flight.IsInternational ?? false,
    travelDateFrom: segment?.DepartureDate?.replace("Thu-", ""), // Format clean-up if needed
    travelDateTo: segment?.ArrivalDate?.replace("Thu-", ""), // Optional: only if known
    crsName: flight.Fare?.crsName ?? undefined,
  };
}

type BookingData = {
  origin: string;
  destination: string;
  tripType: string;
  cabin: string;
  airline: string;
  fareType?: string;
  isInternational?: boolean;
  travelDateFrom?: string;
  travelDateTo?: string;
  crsName?: string;
};
export function isDateInRange(
  from?: string,
  to?: string,
  rule?: any // You can type it better if you have rule.dateValidation
): boolean {
  if (!rule?.dateValidation) return true;

  const today = new Date();

  // const salesFrom = rule.dateValidation.sales_valid_from
  //   ? new Date(rule.dateValidation.sales_valid_from)
  //   : undefined;
  // const salesTo = rule.dateValidation.sales_valid_to
  //   ? new Date(rule.dateValidation.sales_valid_to)
  //   : undefined;

  // if (salesFrom && today < salesFrom) return false;
  // if (salesTo && today > salesTo) return false;

  const travelFrom = from ? new Date(from) : undefined;
  const travelTo = to ? new Date(to) : undefined;

  if (
    rule.dateValidation.travel_valid_from &&
    travelFrom &&
    travelFrom < new Date(rule.dateValidation.travel_valid_from)
  )
    return false;
  if (
    rule.dateValidation.travel_valid_to &&
    travelTo &&
    travelTo > new Date(rule.dateValidation.travel_valid_to)
  )
    return false;

  return true;
}

export function matchCommissionRule(
  bookingData: BookingData,
  commissionRules: CommissionRule[]
): CommissionRule | undefined {
  const {
    origin,
    destination,
    tripType,
    cabin,
    airline,
    fareType,
    isInternational = false,
    travelDateFrom,
    travelDateTo,
    crsName,
  } = bookingData;

  const today = new Date();
  const airportType = isInternational ? "Intl." : "Dom.";

  const activeRules = commissionRules.filter((rule) => {
    return (
      rule.isActive &&
      (!rule.validUpto ||
        (new Date(rule.validUpto) >= today &&
          !!travelDateFrom &&
          new Date(travelDateFrom) <= new Date(rule.validUpto)))
    );
  });

  for (const rule of activeRules) {
    // ✅ Airline match (if provided)
    if (rule.airlineCodes?.length && !rule.airlineCodes.includes(airline)) {
      continue;
    }

    // ✅ Cabin match
    if (rule.cabin && rule.cabin !== "Both" && rule.cabin !== cabin) {
      continue;
    }
    // ✅ Fare type match
    if (fareType && rule.fareType && rule.fareType !== fareType) {
      continue;
    }
    // ✅ Airport type match
    if (
      rule.airport &&
      rule.airport !== "both" &&
      rule.airport !== airportType
    ) {
      continue;
    }

    // ✅ CRS match
    if (crsName && rule.crsName && rule.crsName !== crsName) {
      continue;
    }

    // ✅ Trip type match
    if (
      tripType &&
      rule?.otherOptions?.tripType &&
      rule.otherOptions.tripType !== tripType
    ) {
      continue;
    }

    // ✅ Origin/Destination match
    if (
      Array.isArray(rule.originCodes) &&
      Array.isArray(rule.destinationCodes)
    ) {
      const originMatch = rule.originCodes.includes(origin);
      const destinationMatch = rule.destinationCodes.includes(destination);
      if (!originMatch || !destinationMatch) {
        continue;
      }
    }

    // ✅ Travel date logic if required
    if (!isDateInRange(travelDateFrom, travelDateTo, rule)) {
      continue;
    }

    // 🟢 All checks passed
    return rule;
  }

  // ❌ No matching rule found
  return undefined;
}

type Passenger = {
  adultCount: number;
  childrenCount: number;
  infantCount: number;
};

export function calculateCommissionAmount(
  rule: CommissionRule | undefined,
  fare: number | string,
  pax: Passenger,
  tripType: "ONE-WAY" | "ROUND-TRIP"
): {
  totalCommission: number;
  appliedRuleId?: number;
} {
  if (!rule) {
    return {
      totalCommission: 0,
    };
  }

  const totalFare = typeof fare === "string" ? parseFloat(fare) : fare;

  const adultsAndChildren = pax.adultCount + pax.childrenCount;

  let totalCalculatedCommission = 0;

  const isPercentage =
    !!rule.per && rule.per !== "" && rule.per !== "0.00" && rule.per !== "0";
  if (isPercentage) {
    totalCalculatedCommission = (Number(rule.per) / 100) * totalFare;
  } else {
    let adultCommission = Number(rule?.adultChildValue || "0");
    let childCommission = Number(rule?.adultChildValue || "0");
    let infantCommission = Number(rule?.infantValue || "0");

    totalCalculatedCommission += pax?.adultCount * adultCommission;
    if (pax.childrenCount > 0) {
      totalCalculatedCommission += pax?.childrenCount * childCommission;
    }
    if (pax.infantCount > 0) {
      totalCalculatedCommission += pax?.infantCount * infantCommission;
    }

    // // flatType adjustment
    // if (rule.flatType === "pax_per_itineary") {
    //   if (tripType === "ROUND-TRIP") {
    //     adultCommission = flatAdult;
    //     infantCommission = flatInfant;
    //   }
    // } else if (rule.flatType === "pax_per_sector") {
    //   const sectors = tripType === "ROUND-TRIP" ? 2 : 1;
    //   adultChildCommission *= sectors;
    //   infantCommission *= sectors;
    // }
    // // For pax_per_trip: no adjustment needed
  }

  const totalCommission = Math.round(totalCalculatedCommission);

  return {
    totalCommission,
    appliedRuleId: rule.id,
  };
}

export const getBrowserName = (): string => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes("Firefox")) return "Firefox";
  if (userAgent.includes("Edg")) return "Edge";
  if (userAgent.includes("Chrome") && !userAgent.includes("Edg"))
    return "Chrome";
  if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
    return "Safari";
  if (userAgent.includes("OPR") || userAgent.includes("Opera")) return "Opera";

  return "Unknown";
};

export function removeUnderscores(text: string): string {
  return text.replace(/_/g, " ");
}

export function capitalizeFirstLetter(str: string) {
  if (!str) return ""; // Handle null, undefined, or empty
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const getChangedFields = (
  original: Record<string, any>,
  current: Record<string, any>
): Record<string, any> => {
  const changed: Record<string, any> = {};

  if (!original) return current;

  Object.keys(current).forEach((key) => {
    const originalValue = original[key];
    const currentValue = current[key];

    if (dayjs.isDayjs(currentValue)) {
      if (!originalValue || !dayjs(originalValue).isSame(currentValue)) {
        changed[key] = currentValue;
      }
    } else if (currentValue !== originalValue) {
      changed[key] = currentValue;
    }
  });

  return changed;
};
export function formatDateToDDMMMYYYY(dateInput: string | Date): string {
  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date input");
  }

  const day: string = String(date.getUTCDate()).padStart(2, "0");
  const monthNames: string[] = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month: string = monthNames[date.getUTCMonth()];
  const year: number = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
}

export const isToday = (date?: string | null): boolean => {
  if (!date) return false;

  let parsed: dayjs.Dayjs | null = null;

  // Accept "YYYY-MM-DD"
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    parsed = dayjs(date, "YYYY-MM-DD");
  }
  // Accept "DD-MM-YYYY"
  else if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
    parsed = dayjs(date, "DD-MM-YYYY");
  }

  // Fallback: agar format unknown ho
  if (!parsed || !parsed.isValid()) return false;

  // Compare only the date part (ignore time)
  return parsed.isSame(dayjs(), "day");
};

// utils/phoneHelper.js
export function splitCountryCode(mobileNumber: string | null | undefined) {
  if (!mobileNumber) return { countryCode: "", number: "" };

  const match = mobileNumber.match(/^\+?(\d{1,3})(\d+)/);

  if (match) {
    return {
      countryCode: `+${match[1]}`, // country code
      number: match[2], // बाकी का नंबर
    };
  }

  return { countryCode: "", number: mobileNumber };
}

export function splitWithLookup(raw: any) {
  if (!raw) return null;
  let s = String(raw).trim();

  // Handle 00 prefix
  if (s.startsWith("00")) s = "+" + s.slice(2);

  // If it doesn't start with +, just add it (assuming it's already in intl format like 91..., 44...)
  if (!s.startsWith("+")) s = "+" + s;

  // Remove everything except digits and leading +
  s = "+" + s.slice(1).replace(/\D/g, "");

  const digits = s.slice(1);

  // try longest match (3 -> 1)
  for (let len = 3; len >= 1; len--) {
    const cc = digits.slice(0, len);
    if (COUNTRY_CODES.has(cc)) {
      const national = digits.slice(len);
      return { countryCode: cc, nationalNumber: national };
    }
  }
  return { error: "", rawDigits: digits };
}


